/**
 * Fast location utilities - Optimized location handling for modern URLs
 * Includes city detection for address validation and user address selection
 */

import { CITIES } from '@/store';

export interface CityInfo {
  slug: string;
  id: string;
  label: string;
}

/**
 * Extract lat/lng from URL search params
 * @param searchParams - URL search parameters
 * @returns Object with lat, lng, and hasLocation flag
 */
export function extractLocationFromUrlParams(searchParams: {
  lat?: string | string[];
  lng?: string | string[];
}): {
  lat?: number;
  lng?: number;
  hasLocation: boolean;
} {
  const latParam = Array.isArray(searchParams.lat)
    ? searchParams.lat[0]
    : searchParams.lat;
  const lngParam = Array.isArray(searchParams.lng)
    ? searchParams.lng[0]
    : searchParams.lng;

  if (latParam && lngParam) {
    const lat = parseFloat(latParam);
    const lng = parseFloat(lngParam);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, hasLocation: true };
    }
  }

  return { hasLocation: false };
}

/**
 * Create location params object for API calls
 * @param lat Latitude
 * @param lng Longitude
 * @param defaultRadius Default radius if not provided
 * @returns Object with lat, lng, and radius for API calls
 */
export function createLocationParams(
  lat?: number,
  lng?: number,
  defaultRadius = 10000
): { lat?: number; lng?: number; radius?: number } {
  if (lat && lng) {
    return { lat, lng, radius: defaultRadius };
  }
  return {};
}

/**
 * Generate place_id from coordinates (for backward compatibility)
 * @param lat Latitude
 * @param lng Longitude
 * @returns Place ID string
 */
export function generatePlaceId(lat: number, lng: number): string {
  return `${lat}_${lng}`;
}

/**
 * Detects city based on broad Vietnam regions (North / Central / South)
 * Uses latitude as main divider (approximation)
 * @param lat Latitude
 * @param lng Longitude
 * @returns City information object
 */
export function detectCityFromCoordinates(lat: number, lng: number): CityInfo {
  // Nếu tọa độ không nằm trong phạm vi VN, fallback Đà Nẵng
  if (lat < 8 || lat > 24 || lng < 102 || lng > 110) {
    return {
      slug: 'da-nang',
      id: 'DAD',
      label: 'Đà Nẵng',
    };
  }

  // Giới hạn phân vùng (xấp xỉ)
  // Miền Bắc: lat >= 18.5
  // Miền Trung: 14 → 18.5
  // Miền Nam: < 14
  if (lat >= 18.5) {
    return {
      slug: 'ha-noi',
      id: 'HAN',
      label: 'Hà Nội',
    };
  } else if (lat >= 14 && lat < 18.5) {
    return {
      slug: 'da-nang',
      id: 'DAD',
      label: 'Đà Nẵng',
    };
  } else {
    return {
      slug: 'ho-chi-minh',
      id: 'SGN',
      label: 'TP.HCM',
    };
  }
}

/**
 * Find city info from CITIES array by slug
 * @param citySlug City slug to search for
 * @returns City from CITIES array or null
 */
export function findCityBySlug(citySlug: string) {
  return CITIES.find((city) => city.slug === citySlug);
}

/**
 * Detect city from address text (fallback method)
 * @param address Address string to search in
 * @returns Matched city from CITIES array or null
 */
export function detectCityFromAddressText(address?: string) {
  if (!address) return null;
  return CITIES.find((city) => address.includes(city.label));
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param point1 First coordinate {lat, lng}
 * @param point2 Second coordinate {lat, lng}
 * @returns Distance in meters
 */
export function calculateDistance(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Create address object from coordinates
 * @param lat Latitude
 * @param lng Longitude
 * @param address Optional address string
 * @returns Basic address object
 */
export function createBasicAddress(lat: number, lng: number, address?: string) {
  return {
    lat,
    lng,
    address: address ?? `${lat}, ${lng}`,
    place_id: generatePlaceId(lat, lng),
    second_label: address ?? 'Vị trí của bạn',
  };
}
